<template>
  <BlockFieldset class="project-power-company-communication">
    <p v-if="isNewProject" class="text-muted">{{ $t('saveNewProjectFirst') }}</p>
    <div v-else>
      <template v-for="(fieldGroup, groupIndex) in FIELD_GROUPS">
        <hr v-if="groupIndex > 0" :key="`separator-${groupIndex}`" />
        <b-form-group
          v-for="field in fieldGroup"
          :key="field.name"
          horizontal
          :label="$t(field.name)"
          :label-for="field.name"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <Calendar
            v-if="field.isDate"
            :value="$calendarFormat(project[field.name])"
            @input="setField(field.name, $event)"
            :inputId="field.name"
            selectionMode="single"
            class="p-column-filter"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date')"
            :showWeek="true"
            :yearNavigator="true"
            :monthNavigator="true"
            :yearRange="yearRange"
            :disabled="isBlockReadOnly"
          >
          </Calendar>
          <b-form-input
            v-else
            :id="field.name"
            :value="project[field.name]"
            @input="setField(field.name, $event)"
            type="text"
            :placeholder="$t(field.name)"
          />
        </b-form-group>
      </template>

      <legend>
        <strong>{{ $t('notes') }}</strong>
      </legend>
      <b-form-group horizontal breakpoint="md">
        <ckeditor
          :editor="editor"
          :value="project.powerCompanyDescription"
          @input="setField('powerCompanyDescription', $event)"
          :config="editorConfig"
          :disabled="isBlockReadOnly"
        ></ckeditor>
      </b-form-group>

      <div v-if="!isBlockReadOnly" class="text-right mt-2">
        <b-button variant="bg" :disabled="isSaving" @click="save">
          {{ $t('saveNamed', { name: $t('powerCompanyCommunication') }) }}
        </b-button>
      </div>
    </div>
  </BlockFieldset>
</template>

<script>
import { mapActions } from 'vuex';
import Calendar from 'primevue/calendar';
import CKEditor from '@ckeditor/ckeditor5-vue2';
import InlineEditor from '@ckeditor/ckeditor5-build-inline';
import BlockFieldset from '@/components/Access/BlockFieldset.vue';
import { AccessBlockMixin } from '@/mixins/accessBlock';
import { BlockSaveMixin } from '@/mixins/blockSave';
import { toProjectPowerCompanyCommunicationInput } from './projectPowerCompanyCommunicationInput';

/** Number of the unsaved project (ProjectEdit `_new`): the block needs a saved project. */
const NEW_PROJECT_NUMBER = '_new';

/** First year of the year navigator, as in the previous view; the last one is the next year. */
const FIRST_SELECTABLE_YEAR = 2019;

/** Dates and project numbers in the order and grouping of the previous view, groups split by a line. */
const FIELD_GROUPS = [
  [
    { name: 'netRegistrationSentAt', isDate: true },
    { name: 'powerCompanyProjectNumber', isDate: false },
  ],
  [{ name: 'injectionConfirmationReceivedAt', isDate: true }],
  [
    { name: 'operationRegistrationSentAt', isDate: true },
    { name: 'powerCompanyProjectNumber2', isDate: false },
  ],
  [
    { name: 'completionMessageSentAt', isDate: true },
    { name: 'powerCompanyProjectNumber3', isDate: false },
  ],
  [{ name: 'powerCompanyClearanceAt', isDate: true }],
];

/**
 * Block project-power-company-communication ("EVU Kommunikation", see feature page projekt-evu):
 * five dates, three project numbers of the power company and the notes. On the unsaved project it
 * shows only a hint. Emits `dirty` and `saved`.
 */
export default {
  name: 'ProjectPowerCompanyCommunication',
  components: { BlockFieldset, Calendar, ckeditor: CKEditor.component },
  mixins: [AccessBlockMixin, BlockSaveMixin],
  props: {
    blockId: { type: String, required: true },
    project: { type: Object, required: true },
  },
  data() {
    return {
      FIELD_GROUPS,
      editor: InlineEditor,
      editorConfig: {},
    };
  },
  computed: {
    isNewProject() {
      return this.project.number === NEW_PROJECT_NUMBER;
    },
    blockInput() {
      return toProjectPowerCompanyCommunicationInput(this.project);
    },
    yearRange() {
      return `${FIRST_SELECTABLE_YEAR}:${new Date().getFullYear() + 1}`;
    },
  },
  methods: {
    ...mapActions(['updateProjectPowerCompanyCommunication']),
    saveBlockInput(input) {
      return this.updateProjectPowerCompanyCommunication({
        projectNumber: this.project.number,
        input,
      });
    },
    setField(field, value) {
      this.$set(this.project, field, value);
    },
  },
};
</script>
